/* eslint-disable max-lines */
import { IAppState, IContext, IThunkActionCreator } from '.';
import { getReviews, rateAnswer, rateReview } from '../api/reviews';
import { IGetReviewsRequest, IReview, IReviewAnswer, TReviewUserRateStatus } from '../types/review';
import { IAnswerFormSubmitSucceed } from './answer_form';
import { IReviewFormSubmitSucceed } from './review_form';
import { TThunkDispatch } from '../types/redux';

export interface IReviewsData {
  entityRate: number | null;
  entityRateMax: number | null;
  entityRateMin: number | null;
  reviews: IReview[];
  isUserSubscribed: boolean;
  subscriptionEmail: string | null;
  totalCount: number;
  approvedReviewsCount: number;
}

export interface IReviewsRequested {
  type: 'IReviewsRequested';
}

export interface IReviewsTotalCountIncrement {
  type: 'IReviewsTotalCountIncrement';
}

export interface IReviewsFetched {
  entityRate: number | null;
  entityRateMax: number | null;
  entityRateMin: number | null;
  reviews: IReview[];
  totalCount: number;
  approvedReviewsCount: number;
  isUserSubscribed: boolean;
  subscriptionEmail: string;
  type: 'IReviewsFetched';
}

export function reviewsTotalCountIncrement(): IReviewsTotalCountIncrement {
  return {
    type: 'IReviewsTotalCountIncrement',
  };
}

export function fetchReviews(options: IGetReviewsRequest): IThunkActionCreator {
  return (dispatch: TThunkDispatch, getState: () => IAppState, context: IContext) => {
    dispatch<IReviewsRequested>({
      type: 'IReviewsRequested',
    });
    const state = getState();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return getReviews(context.httpApi, options as any, context.logger).then((reviewsData: any) => {
      const stateReviews = (Array.isArray(state.reviews.reviews) && state.reviews.reviews) || [];
      const nextReviews = (Array.isArray(reviewsData.reviews) && reviewsData.reviews) || [];

      return dispatch<IReviewsFetched>({
        approvedReviewsCount: reviewsData.approvedReviewsCount,
        entityRate: reviewsData.entityRate,
        entityRateMax: reviewsData.entityRateMax,
        entityRateMin: reviewsData.entityRateMin,
        isUserSubscribed: reviewsData.isUserSubscribed,
        reviews: [...stateReviews, ...nextReviews],
        subscriptionEmail: reviewsData.subscriptionEmail,
        totalCount: reviewsData.totalCount,
        type: 'IReviewsFetched',
      });
    });
  };
}

export interface IReviewLiked {
  type: 'IReviewLiked';
  reviewId: string;
}

export interface IReviewLikeFailed {
  type: 'IReviewLikeFailed';
  reviewId: string;
  userLikeStatus: TReviewUserRateStatus;
}

export function likeReview(reviewId: string): IThunkActionCreator {
  return (dispatch: TThunkDispatch, getState: () => IAppState, context: IContext) => {
    const {
      pageData,
      reviews: { reviews },
    } = getState();
    const prevReview = reviews.find(review => review.reviewId === reviewId);
    if (!prevReview) {
      return;
    }

    dispatch<IReviewLiked>({
      reviewId,
      type: 'IReviewLiked',
    });

    return rateReview(context.httpApi, {
      entityId: pageData.entityId,
      entityType: pageData.entityType,
      reviewId,
      status: 'liked',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any).catch(() => {
      return dispatch<IReviewLikeFailed>({
        reviewId,
        type: 'IReviewLikeFailed',
        userLikeStatus: prevReview.userLikeStatus,
      });
    });
  };
}

export interface IReviewDisliked {
  type: 'IReviewDisliked';
  reviewId: string;
}

export interface IReviewDislikeFailed {
  type: 'IReviewDislikeFailed';
  reviewId: string;
  userLikeStatus: TReviewUserRateStatus;
}

export function dislikeReview(reviewId: string): IThunkActionCreator {
  return (dispatch: TThunkDispatch, getState: () => IAppState, context: IContext) => {
    const {
      pageData,
      reviews: { reviews },
    } = getState();
    const prevReview = reviews.find(review => review.reviewId === reviewId);
    if (!prevReview) {
      return;
    }

    dispatch<IReviewDisliked>({
      reviewId,
      type: 'IReviewDisliked',
    });

    return rateReview(context.httpApi, {
      entityId: pageData.entityId,
      entityType: pageData.entityType,
      reviewId,
      status: 'disliked',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any).catch(() => {
      return dispatch<IReviewDislikeFailed>({
        reviewId,
        type: 'IReviewDislikeFailed',
        userLikeStatus: prevReview.userLikeStatus,
      });
    });
  };
}

export interface IReviewAnswerLiked {
  type: 'IReviewAnswerLiked';
  reviewId: string;
  answerId: string;
}

export interface IReviewAnswerLikeFailed {
  type: 'IReviewAnswerLikeFailed';
  reviewId: string;
  answerId: string;
  userLikeStatus: TReviewUserRateStatus;
}

export function likeAnswer(reviewId: string, answerId: string): IThunkActionCreator {
  return (dispatch: TThunkDispatch, getState: () => IAppState, context: IContext) => {
    const {
      pageData,
      reviews: { reviews },
    } = getState();

    const prevReview = reviews.find(review => review.reviewId === reviewId);
    if (!prevReview) {
      return;
    }

    const prevAnswer = prevReview.answers.find(answer => answer.answerId === answerId);
    if (!prevAnswer) {
      return;
    }

    dispatch<IReviewAnswerLiked>({
      answerId,
      reviewId,
      type: 'IReviewAnswerLiked',
    });

    return rateAnswer(context.httpApi, {
      answerId,
      entityId: pageData.entityId,
      entityType: pageData.entityType,
      reviewId,
      status: 'liked',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any).catch(() => {
      return dispatch<IReviewAnswerLikeFailed>({
        answerId,
        reviewId,
        type: 'IReviewAnswerLikeFailed',
        userLikeStatus: prevAnswer.userLikeStatus,
      });
    });
  };
}

export interface IReviewAnswerDisliked {
  type: 'IReviewAnswerDisliked';
  reviewId: string;
  answerId: string;
}

export interface IReviewAnswerDislikeFailed {
  type: 'IReviewAnswerDislikeFailed';
  reviewId: string;
  answerId: string;
  userLikeStatus: TReviewUserRateStatus;
}

export function dislikeAnswer(reviewId: string, answerId: string): IThunkActionCreator {
  return (dispatch: TThunkDispatch, getState: () => IAppState, context: IContext) => {
    const {
      pageData,
      reviews: { reviews },
    } = getState();

    const prevReview = reviews.find(review => review.reviewId === reviewId);
    if (!prevReview) {
      return;
    }

    const prevAnswer = prevReview.answers.find(answer => answer.answerId === answerId);
    if (!prevAnswer) {
      return;
    }

    dispatch<IReviewAnswerDisliked>({
      answerId,
      reviewId,
      type: 'IReviewAnswerDisliked',
    });

    return rateAnswer(context.httpApi, {
      answerId,
      entityId: pageData.entityId,
      entityType: pageData.entityType,
      reviewId,
      status: 'disliked',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any).catch(() => {
      return dispatch<IReviewAnswerDislikeFailed>({
        answerId,
        reviewId,
        type: 'IReviewAnswerDislikeFailed',
        userLikeStatus: prevAnswer.userLikeStatus,
      });
    });
  };
}

export type TReviewsActions =
  | IReviewsRequested
  | IReviewsTotalCountIncrement
  | IReviewsFetched
  | IReviewLiked
  | IReviewLikeFailed
  | IReviewDisliked
  | IReviewDislikeFailed
  | IReviewAnswerLiked
  | IReviewAnswerLikeFailed
  | IReviewAnswerDisliked
  | IReviewAnswerDislikeFailed
  | IReviewFormSubmitSucceed
  | IAnswerFormSubmitSucceed;

export function reviewsReducer(state: IReviewsData, action: TReviewsActions): IReviewsData {
  switch (action.type) {
    case 'IReviewsTotalCountIncrement':
      return {
        ...state,
        totalCount: ++state.totalCount,
      };

    case 'IReviewsRequested':
      return {
        ...state,
      };

    case 'IReviewsFetched':
      return {
        ...state,
        approvedReviewsCount: action.approvedReviewsCount,
        entityRate: action.entityRate,
        entityRateMax: action.entityRateMax,
        entityRateMin: action.entityRateMin,
        isUserSubscribed: action.isUserSubscribed,
        reviews: action.reviews,
        subscriptionEmail: action.subscriptionEmail,
        totalCount: action.totalCount,
      };

    case 'IReviewLiked': {
      const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
      if (!prevReview) {
        return state;
      }

      const nextReview: IReview = {
        ...prevReview,
        dislikesCount:
          prevReview.userLikeStatus === 'disliked' ? prevReview.dislikesCount - 1 : prevReview.dislikesCount,
        likesCount: prevReview.likesCount + 1,
        userLikeStatus: 'liked',
      };

      const nextReviews = state.reviews.concat();
      nextReviews[nextReviews.indexOf(prevReview)] = nextReview;

      return {
        ...state,
        reviews: nextReviews,
      };
    }

    case 'IReviewLikeFailed': {
      const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
      if (!prevReview) {
        return state;
      }

      const nextReview: IReview = {
        ...prevReview,
        dislikesCount: action.userLikeStatus === 'disliked' ? prevReview.dislikesCount + 1 : prevReview.dislikesCount,
        likesCount: prevReview.likesCount - 1,
        userLikeStatus: action.userLikeStatus,
      };

      const nextReviews = state.reviews.concat();
      nextReviews[nextReviews.indexOf(prevReview)] = nextReview;

      return {
        ...state,
        reviews: nextReviews,
      };
    }

    case 'IReviewDisliked': {
      const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
      if (!prevReview) {
        return state;
      }

      const nextReview: IReview = {
        ...prevReview,
        dislikesCount: prevReview.dislikesCount + 1,
        likesCount: prevReview.userLikeStatus === 'liked' ? prevReview.likesCount - 1 : prevReview.likesCount,
        userLikeStatus: 'disliked',
      };

      const nextReviews = state.reviews.concat();
      nextReviews[nextReviews.indexOf(prevReview)] = nextReview;

      return {
        ...state,
        reviews: nextReviews,
      };
    }

    case 'IReviewDislikeFailed': {
      const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
      if (!prevReview) {
        return state;
      }

      const nextReview: IReview = {
        ...prevReview,
        dislikesCount: prevReview.dislikesCount - 1,
        likesCount: action.userLikeStatus === 'liked' ? prevReview.likesCount + 1 : prevReview.likesCount,
        userLikeStatus: action.userLikeStatus,
      };

      const nextReviews = state.reviews.concat();
      nextReviews[nextReviews.indexOf(prevReview)] = nextReview;

      return {
        ...state,
        reviews: nextReviews,
      };
    }

    case 'IReviewFormSubmitSucceed': {
      if (!action.edit) {
        return {
          ...state,
          reviews: state.reviews.concat(action.review),
        };
      } else {
        const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
        if (!prevReview) {
          return state;
        }

        const nextReviews = state.reviews.concat();
        nextReviews[nextReviews.indexOf(prevReview)] = action.review;

        return {
          ...state,
          reviews: nextReviews,
        };
      }
    }

    case 'IAnswerFormSubmitSucceed': {
      const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
      if (!prevReview) {
        return state;
      }

      let nextReview: IReview;
      if (!action.edit) {
        nextReview = {
          ...prevReview,
          answers: prevReview.answers.concat(action.answer),
        };
      } else {
        const prevAnswer = prevReview.answers.find(answer => answer.answerId === action.answerId);
        if (!prevAnswer) {
          return state;
        }

        const nextAnswers = prevReview.answers.concat();
        nextAnswers[nextAnswers.indexOf(prevAnswer)] = action.answer;

        nextReview = {
          ...prevReview,
          answers: nextAnswers,
        };
      }

      const nextReviews = state.reviews.concat();
      nextReviews[nextReviews.indexOf(prevReview)] = nextReview;

      return {
        ...state,
        reviews: nextReviews,
      };
    }

    case 'IReviewAnswerLiked': {
      const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
      if (!prevReview) {
        return state;
      }

      const prevAnswer = prevReview.answers.find(answer => answer.answerId === action.answerId);
      if (!prevAnswer) {
        return state;
      }

      const nextAnswer: IReviewAnswer = {
        ...prevAnswer,
        dislikesCount:
          prevAnswer.userLikeStatus === 'disliked' ? prevAnswer.dislikesCount - 1 : prevAnswer.dislikesCount,
        likesCount: prevAnswer.likesCount + 1,
        userLikeStatus: 'liked',
      };

      const nextAnswers = prevReview.answers.concat();
      nextAnswers[nextAnswers.indexOf(prevAnswer)] = nextAnswer;

      const nextReview: IReview = {
        ...prevReview,
        answers: nextAnswers,
      };

      const nextReviews = state.reviews.concat();
      nextReviews[nextReviews.indexOf(prevReview)] = nextReview;

      return {
        ...state,
        reviews: nextReviews,
      };
    }

    case 'IReviewAnswerLikeFailed': {
      const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
      if (!prevReview) {
        return state;
      }

      const prevAnswer = prevReview.answers.find(answer => answer.answerId === action.answerId);
      if (!prevAnswer) {
        return state;
      }

      const nextAnswer: IReviewAnswer = {
        ...prevAnswer,
        dislikesCount: action.userLikeStatus === 'disliked' ? prevAnswer.dislikesCount + 1 : prevAnswer.dislikesCount,
        likesCount: prevAnswer.likesCount - 1,
        userLikeStatus: action.userLikeStatus,
      };

      const nextAnswers = prevReview.answers.concat();
      nextAnswers[nextAnswers.indexOf(prevAnswer)] = nextAnswer;

      const nextReview: IReview = {
        ...prevReview,
        answers: nextAnswers,
      };

      const nextReviews = state.reviews.concat();
      nextReviews[nextReviews.indexOf(prevReview)] = nextReview;

      return {
        ...state,
        reviews: nextReviews,
      };
    }

    case 'IReviewAnswerDisliked': {
      const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
      if (!prevReview) {
        return state;
      }

      const prevAnswer = prevReview.answers.find(answer => answer.answerId === action.answerId);
      if (!prevAnswer) {
        return state;
      }

      const nextAnswer: IReviewAnswer = {
        ...prevAnswer,
        dislikesCount: prevAnswer.dislikesCount + 1,
        likesCount: prevAnswer.userLikeStatus === 'liked' ? prevAnswer.likesCount - 1 : prevAnswer.likesCount,
        userLikeStatus: 'disliked',
      };

      const nextAnswers = prevReview.answers.concat();
      nextAnswers[nextAnswers.indexOf(prevAnswer)] = nextAnswer;

      const nextReview: IReview = {
        ...prevReview,
        answers: nextAnswers,
      };

      const nextReviews = state.reviews.concat();
      nextReviews[nextReviews.indexOf(prevReview)] = nextReview;

      return {
        ...state,
        reviews: nextReviews,
      };
    }

    case 'IReviewAnswerDislikeFailed': {
      const prevReview = state.reviews.find(review => review.reviewId === action.reviewId);
      if (!prevReview) {
        return state;
      }

      const prevAnswer = prevReview.answers.find(answer => answer.answerId === action.answerId);
      if (!prevAnswer) {
        return state;
      }

      const nextAnswer: IReviewAnswer = {
        ...prevAnswer,
        dislikesCount: prevAnswer.dislikesCount - 1,
        likesCount: action.userLikeStatus === 'liked' ? prevAnswer.likesCount + 1 : prevAnswer.likesCount,
        userLikeStatus: action.userLikeStatus,
      };

      const nextAnswers = prevReview.answers.concat();
      nextAnswers[nextAnswers.indexOf(prevAnswer)] = nextAnswer;

      const nextReview: IReview = {
        ...prevReview,
        answers: nextAnswers,
      };

      const nextReviews = state.reviews.concat();
      nextReviews[nextReviews.indexOf(prevReview)] = nextReview;

      return {
        ...state,
        reviews: nextReviews,
      };
    }

    default:
      return state || {};
  }
}
